import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  AfterViewInit,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';

import { StaticMessage } from '@shared/enums';
import { DateRangePreset } from '@dpr/models';
import { endOf, startOf, subtract } from '@shared/utils';

@Component({
  selector: 'app-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent implements OnInit, AfterViewInit {
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() useExplicitActions = true;

  @Output() cancel = new EventEmitter();
  @Output() dateRangeChanged = new EventEmitter<{
    startDate: Date;
    endDate: Date;
  }>();

  @ViewChild('startCalendar') startCalendar: MatCalendar<Date>;
  @ViewChild('endCalendar') endCalendar: MatCalendar<Date>;

  presetRanges: DateRangePreset[] = [];

  defaultStartDateLabel = $localize`:@@dpr.dateRangePicker.txtDefaultStartDate:Start Date`;
  defaultEndDateLabel = $localize`:@@dpr.dateRangePicker.txtDefaultEndDate:End Date`;

  StaticMessage = StaticMessage;

  minimumStartDate: Date;
  maximumEndDate: Date;

  constructor(private cdr: ChangeDetectorRef) {}

  get today(): Date {
    return new Date();
  }

  ngOnInit(): void {
    this.minimumStartDate = new Date(this.minDate);
    this.maximumEndDate = new Date(this.maxDate);
    this.presetRanges = this.getPresetRanges();
  }

  ngAfterViewInit(): void {
    this.startCalendar._goToDateInView(this.startDate ?? new Date(), 'month');
    this.endCalendar._goToDateInView(this.endDate ?? new Date(), 'month');

    this.cdr.detectChanges();
  }

  onRangeSelection(range: DateRangePreset): void {
    this.startDate = range.dateRange.startDate;
    this.endDate = range.dateRange.endDate;
    this.minimumStartDate = range.dateRange.startDate;
    this.maximumEndDate = range.dateRange.endDate;

    this.setRange(this.startDate, this.endDate);

    if (!this.useExplicitActions) {
      this.dateRangeChanged.emit({
        startDate: this.startDate ?? null,
        endDate: this.endDate ?? null,
      });
    }
  }

  onApply(): void {
    this.dateRangeChanged.emit({
      startDate: this.startDate ?? null,
      endDate: this.endDate ?? null,
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onClear(): void {
    this.startDate = null;
    this.endDate = null;

    this.dateRangeChanged.emit({
      startDate: this.startDate,
      endDate: this.endDate,
    });
  }

  private getPresetRanges(): DateRangePreset[] {
    return [
      {
        name: $localize`:@@dpr.dateRangePicker.btnAllTime:All time`,
        dateRange: {
          startDate: this.minimumStartDate,
          endDate: this.maximumEndDate,
        },
      },
      {
        name: $localize`:@@dpr.dateRangePicker.btnThisWeek:This week`,
        dateRange: {
          startDate: startOf(this.today, 'week'),
          endDate: endOf(this.today, 'week'),
        },
      },
      {
        name: $localize`:@@dpr.dateRangePicker.btnThisMonth:This month`,
        dateRange: {
          startDate: startOf(this.today, 'month'),
          endDate: endOf(this.today, 'month'),
        },
      },
      {
        name: $localize`:@@dpr.dateRangePicker.btnLasMonth:Last month`,
        dateRange: {
          startDate: startOf(subtract(this.today, 1, 'month'), 'month'),
          endDate: endOf(subtract(this.today, 1, 'month'), 'month'),
        },
      },
      {
        name: $localize`:@@dpr.dateRangePicker.btnLastYear:Last year`,
        dateRange: {
          startDate: startOf(subtract(this.today, 1, 'year'), 'year'),
          endDate: endOf(subtract(this.today, 1, 'year'), 'year'),
        },
      },
      {
        name: $localize`:@@dpr.dateRangePicker.btnCustomRange:Custom range`,
        dateRange: {},
        isCustomRange: true,
      },
    ];
  }

  private setRange(start: Date, end: Date): void {
    setTimeout(() => {
      this.startCalendar._goToDateInView(start ?? new Date(), 'month');
      this.endCalendar._goToDateInView(end ?? new Date(), 'month');
    });
  }
}
